/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils/mappers.ts (Callback Profiles)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Mapping functions for Callback Profiles API responses.

────────────────────────────────────────────────────────────────*/

import {
  FormState,
  CallbackProfileResponse,
  MappedFormState,
  CallbackStrategy,
} from '../types';
import { KNOWN_SETTINGS_KEYS } from './settingsHelpers';

export const mapResponseToState = (profile: CallbackProfileResponse): MappedFormState => {
  const settings =
    profile.settings && typeof profile.settings === 'object'
      ? (profile.settings as Record<string, unknown>)
      : {};

  const extraSettings: Record<string, unknown> = {};
  Object.entries(settings).forEach(([key, value]) => {
    if (!KNOWN_SETTINGS_KEYS.has(key)) {
      extraSettings[key] = value;
    }
  });

  const getStringSetting = (key: string, fallback = '') => {
    const value = settings[key];
    return typeof value === 'string' ? value : fallback;
  };

  const getNumberSetting = (key: string, fallback = 0) => {
    const value = settings[key];
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    }
    return fallback;
  };

  const asStrategy = (value: unknown, fallback: CallbackStrategy): CallbackStrategy => {
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'same-queue' || normalized === 'overflow' || normalized === 'any-agent') {
        return normalized;
      }
    }
    return fallback;
  };

  const retryIntervalValue = getNumberSetting('retry_interval', 300);
  const retryIntervalString = String(retryIntervalValue);

  const formState: FormState = {
    name: profile.name ?? '',
    description: profile.description ?? '',
    callbackKey: getStringSetting('callback_key', '1'),
    confirmSound: getStringSetting('confirm_sound', 'ivr/press_1_to_confirm.wav'),
    thanksSound: getStringSetting('thanks_sound', 'ivr/thank_you_callback.wav'),
    timeoutSeconds: String(getNumberSetting('timeout_seconds', 3600)),
    retryInterval: retryIntervalString,
    maxAttempts: String(getNumberSetting('max_attempts', 3)),
    strategy: asStrategy(settings.strategy, 'same-queue'),
    announcementSound: getStringSetting('announcement_sound', 'ivr/please_wait_callback.wav'),
    enabled: Boolean(profile.enabled),
    createdAt: profile.createdAt ?? null,
    updatedAt: profile.updatedAt ?? null
  };

  return { formState, extraSettings };
};

